import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
  Build as ReworkIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

const ReworkAnalysis = () => {
  const [filters, setFilters] = useState({
    from_date: new Date(new Date().setMonth(new Date().getMonth() - 3)).toISOString().split('T')[0],
    to_date: new Date().toISOString().split('T')[0],
    die_no: '',
    furnace_code: '',
    ht_code: '',
    rework_only: 'all'
  });

  const { enqueueSnackbar } = useSnackbar();
  const { api } = useAuth();

  const { data: reportData, isLoading, error, refetch } = useQuery(
    ['reworkAnalysis', filters],
    async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      // This would call a backend API that analyzes rework data from HT Production
      const response = await api.get(`/heat-treatment/rework-analysis?${params}`);
      return response.data;
    },
    {
      onError: (error) => {
        enqueueSnackbar(`Error fetching rework analysis: ${error.message}`, { variant: 'error' });
      },
      enabled: false // Don't auto-fetch until API is implemented
    }
  );

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    enqueueSnackbar('Download functionality will be implemented', { variant: 'info' });
  };

  const handleGenerateReport = () => {
    refetch();
  };

  const getReworkRateColor = (rate) => {
    if (rate < 2) return 'success';
    if (rate < 5) return 'warning';
    return 'error';
  };

  // Mock data for demonstration since API isn't implemented yet
  const mockData = [
    {
      die_no: 'D12345',
      part_name: 'Connecting Rod',
      total_production: 850,
      rework_count: 15,
      rework_rate: 1.76,
      primary_reason: 'Hardness Variation',
      furnace_code: 'F001',
      ht_code: 'H',
      trend: 'improving'
    },
    {
      die_no: 'D67890',
      part_name: 'Crankshaft',
      total_production: 320,
      rework_count: 18,
      rework_rate: 5.63,
      primary_reason: 'Grain Size Issues',
      furnace_code: 'F002',
      ht_code: 'H',
      trend: 'stable'
    },
    {
      die_no: 'D11111',
      part_name: 'Gear Blank',
      total_production: 1200,
      rework_count: 8,
      rework_rate: 0.67,
      primary_reason: 'Surface Defects',
      furnace_code: 'F001',
      ht_code: 'A',
      trend: 'improving'
    },
    {
      die_no: 'D22222',
      part_name: 'Valve Component',
      total_production: 680,
      rework_count: 34,
      rework_rate: 5.0,
      primary_reason: 'Temperature Control',
      furnace_code: 'F003',
      ht_code: 'T',
      trend: 'worsening'
    }
  ];

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving': return <TrendingUpIcon color="success" />;
      case 'worsening': return <WarningIcon color="error" />;
      default: return <TrendingUpIcon color="action" />;
    }
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading rework analysis: {error.message}
        </Alert>
      </Box>
    );
  }

  const totalProduction = mockData.reduce((sum, item) => sum + item.total_production, 0);
  const totalRework = mockData.reduce((sum, item) => sum + item.rework_count, 0);
  const overallReworkRate = totalProduction > 0 ? (totalRework / totalProduction) * 100 : 0;

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <ReworkIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" gutterBottom color="primary">
          Heat Treatment Rework Analysis
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Analysis Filters</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={filters.from_date}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={filters.to_date}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Die Number"
              value={filters.die_no}
              onChange={(e) => setFilters({ ...filters, die_no: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Furnace Code"
              value={filters.furnace_code}
              onChange={(e) => setFilters({ ...filters, furnace_code: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter Type</InputLabel>
              <Select
                value={filters.rework_only}
                label="Filter Type"
                onChange={(e) => setFilters({ ...filters, rework_only: e.target.value })}
              >
                <MenuItem value="all">All Production</MenuItem>
                <MenuItem value="rework_only">Rework Only</MenuItem>
                <MenuItem value="high_rate">High Rework Rate (&gt;3%)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={handleGenerateReport}
              startIcon={<SearchIcon />}
            >
              Analyze
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box mb={3} display="flex" gap={2}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Export to Excel
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="primary">
                    {totalProduction}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Production
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="error.main">
                    {totalRework}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Rework
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="h5" 
                    color={getReworkRateColor(overallReworkRate)}
                  >
                    {overallReworkRate.toFixed(2)}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Overall Rework Rate
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="info.main">
                    {mockData.filter(item => item.rework_rate > 3).length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    High Risk Dies
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Rework Analysis Table */}
          <Paper>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                Rework Analysis by Die
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Period: {filters.from_date} to {filters.to_date}
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Die Number</strong></TableCell>
                    <TableCell><strong>Part Name</strong></TableCell>
                    <TableCell align="right"><strong>Production</strong></TableCell>
                    <TableCell align="right"><strong>Rework Count</strong></TableCell>
                    <TableCell align="center"><strong>Rework Rate</strong></TableCell>
                    <TableCell><strong>Primary Reason</strong></TableCell>
                    <TableCell><strong>Furnace</strong></TableCell>
                    <TableCell><strong>HT Code</strong></TableCell>
                    <TableCell align="center"><strong>Trend</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No rework analysis data found for the selected criteria
                      </TableCell>
                    </TableRow>
                  ) : (
                    mockData.map((record, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Chip 
                            label={record.die_no} 
                            color="primary" 
                            variant="outlined" 
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{record.part_name}</TableCell>
                        <TableCell align="right">{record.total_production}</TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            color={record.rework_count > 0 ? "error.main" : "textPrimary"}
                            fontWeight={record.rework_count > 20 ? "bold" : "normal"}
                          >
                            {record.rework_count}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Box sx={{ width: '60px', mr: 1 }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={Math.min(record.rework_rate * 10, 100)} 
                                color={getReworkRateColor(record.rework_rate)}
                                sx={{ height: 6, borderRadius: 3 }}
                              />
                            </Box>
                            <Typography 
                              variant="body2" 
                              color={getReworkRateColor(record.rework_rate)}
                              fontWeight="bold"
                            >
                              {record.rework_rate.toFixed(2)}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{record.primary_reason}</TableCell>
                        <TableCell>
                          <Chip 
                            label={record.furnace_code} 
                            color="secondary" 
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={record.ht_code} 
                            color="info" 
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {getTrendIcon(record.trend)}
                            <Typography 
                              variant="caption" 
                              sx={{ ml: 0.5 }}
                              color={record.trend === 'improving' ? 'success.main' : 
                                     record.trend === 'worsening' ? 'error.main' : 'text.secondary'}
                            >
                              {record.trend}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ReworkAnalysis;